import { type FC, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";

import { CourseSelectionList } from "./CourseSelectionList";
import { MoveCoursesConfirmationModal } from "./MoveCoursesConfirmationModal";

import { COURSE_GROUPING_ID_QUERY_PARAM, COURSE_ID_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { FormField } from "mds/components/FormField";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { storeApi } from "store/index";
import { selectCoursesWithGrouping } from "store/selectors/section";
import { useListSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";
import { useQueryParamsActions } from "utils/routing";

type AddCourseGroupingModalProps = {
  onClose: () => void;
};

type AddCourseGroupingFormValues = {
  name: string;
  selectedCourseIds: string[];
};

export const AddCourseGroupingModal: FC<AddCourseGroupingModalProps> = ({ onClose }) => {
  const form = useForm<AddCourseGroupingFormValues>({
    defaultValues: {
      name: "",
      selectedCourseIds: [],
    },
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const courses = useListSelector(selectCoursesWithGrouping, [
    "id",
    "code",
    "grouping_id",
    "groupingTitle",
  ]);
  const { setQueryParams } = useQueryParamsActions();

  const selectedCourseIds = form.watch("selectedCourseIds");

  const handleCreateGrouping = async () => {
    const { name, selectedCourseIds: courseIds } = form.getValues();
    const newGrouping = await storeApi.course_groupings.create({
      course_ids: courseIds,
      parent_id: null,
      title: name.trim(),
    });

    toastSuccessMessage(t("home.toasts.grouping_created"));
    setQueryParams({
      [COURSE_GROUPING_ID_QUERY_PARAM]: newGrouping.id,
      [COURSE_ID_QUERY_PARAM]: null,
    });
    onClose();
  };

  const onSubmit: SubmitHandler<AddCourseGroupingFormValues> = async ({
    selectedCourseIds: courseIds,
  }) => {
    // Check if any selected course codes belong to a different grouping
    const selectedCourseIdSet = new Set(courseIds);
    const coursesFromOtherGroupings = courses.filter(
      (course) => selectedCourseIdSet.has(course.id) && course.grouping_id !== null,
    );

    if (coursesFromOtherGroupings.length > 0) {
      setShowConfirmation(true);
      return;
    }

    await handleCreateGrouping();
  };

  const toggleCourseChecked = (courseId: string) => {
    const currentIds = form.getValues("selectedCourseIds");
    const codeIndex = currentIds.indexOf(courseId);
    const newSelectedIds =
      codeIndex > -1 ? currentIds.filter((id) => id !== courseId) : [...currentIds, courseId];

    form.setValue("selectedCourseIds", newSelectedIds, { shouldDirty: true });
  };

  if (showConfirmation) {
    const selectedCourseIdSet = new Set(selectedCourseIds);
    const coursesFromOtherGroupings = courses.filter(
      (course) => selectedCourseIdSet.has(course.id) && course.grouping_id !== null,
    );

    return (
      <MoveCoursesConfirmationModal
        courses={coursesFromOtherGroupings}
        onCancel={() => setShowConfirmation(false)}
        onConfirm={handleCreateGrouping}
      />
    );
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3">
          <FormField label={t("home.courses.course_grouping_title_label")} name="name" required>
            {(controlProps) => (
              <TextField
                {...form.register("name", {
                  required: t("error.field_required"),
                  validate: (value) => value.trim().length > 0 || t("error.field_required"),
                })}
                {...controlProps}
                eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Course Grouping`}
                placeholder={t("home.placeholder.course_grouping_name")}
              />
            )}
          </FormField>

          {courses.length > 0 && (
            <FormField
              label={t("home.courses.add_courses_to_grouping_label")}
              name="selectedCourseIds"
            >
              {() => (
                <CourseSelectionList
                  courses={courses}
                  selectedCourseIds={selectedCourseIds}
                  onCourseToggle={toggleCourseChecked}
                />
              )}
            </FormField>
          )}
        </div>

        <div className="mt-8 flex w-full justify-end gap-2">
          <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
            {t("common.cancel")}
          </SquareButton>

          <SquareButton
            kind="accent"
            size="m"
            title={t("common.add.course_grouping")}
            type="submit"
          >
            {t("common.add.course_grouping")}
          </SquareButton>
        </div>
      </form>
    </FormProvider>
  );
};
